import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Clock, XCircle, Crown, Send, Megaphone, X } from "lucide-react";

const GROUPS = [
  { k: "", l: "All" },
  { k: "paid", l: "Paid" },
  { k: "pending", l: "Pending" },
  { k: "abandoned", l: "Abandoned" },
];

const usd = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function StatCard({ label, value, sub, good, bad, testid }) {
  return (
    <div data-testid={testid} className={`border bg-appcard p-4 ${good ? "border-emerald-800/60" : bad ? "border-amber-700/60" : "border-neutral-800"}`}>
      <div className="text-[10px] tracking-widest text-neutral-500">{label}</div>
      <div className={`heading text-2xl mt-1 ${good ? "text-emerald-400" : bad ? "text-amber-400" : "text-white"}`}>{value}</div>
      {sub && <div className="text-[10px] text-neutral-500 mt-0.5">{sub}</div>}
    </div>
  );
}

const groupBadge = (g) => ({
  paid: "border-emerald-600 text-emerald-400",
  pending: "border-amber-600 text-amber-400",
  abandoned: "border-neutral-700 text-neutral-500",
}[g] || "border-neutral-700 text-neutral-500");

function ResendOfferButton({ txn, onSent }) {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/revenue/${txn.session_id}/resend-offer`);
      toast.success(`Recovery offer sent to ${data.sent_to}`);
      onSent(txn.session_id, data.sent_at);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send offer");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button data-testid={`resend-offer-${txn.session_id}`} onClick={send} disabled={busy}
      className="flex items-center gap-1.5 px-2.5 py-1 border border-volt text-volt text-[9px] tracking-widest hover:bg-volt hover:text-white transition-colors disabled:opacity-40">
      <Send size={10} /> {busy ? "SENDING…" : txn.recovery_email_sent_at ? "SEND AGAIN" : "RESEND OFFER"}
    </button>
  );
}

function PriceDropModal({ onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.post("/admin/revenue/price-drop-blast?dry_run=true").then(({ data }) => setPreview(data))
      .catch((e) => { toast.error(e.response?.data?.detail || "Preview failed"); onClose(); });
  }, []);

  const fire = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/revenue/price-drop-blast?dry_run=false");
      toast.success(`Price-drop blast: ${data.sent} sent, ${data.failed} failed`,
        data.stopped_reason ? { description: `Stopped early: ${data.stopped_reason}` } : undefined);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Blast failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="price-drop-modal">
      <div className="bg-appcard border border-neutral-700 max-w-2xl w-full max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-800">
          <span className="heading text-lg">PRICE-DROP BLAST — CONFIRM</span>
          <button data-testid="price-drop-close" onClick={onClose} className="text-neutral-500 hover:text-white"><X size={16} /></button>
        </div>
        <div className="px-5 py-4 overflow-y-auto text-xs text-neutral-300">
          {!preview ? "Building recipient preview…" : (
            <>
              <p className="text-neutral-400">
                One-off announcement (coaches free · job posting free · Program Recruiting $5/mo) to everyone who abandoned a Stripe checkout.
                Each person can only ever receive this blast once — already-blasted, subscribed, unsubscribed and bounced
                recipients are skipped automatically.
              </p>
              <div className="mt-3 flex gap-4">
                <span className="heading text-emerald-400" data-testid="price-drop-sendable">{preview.sendable} WILL RECEIVE IT</span>
                <span className="heading text-neutral-500">{preview.skipped} SKIPPED</span>
              </div>
              <div className="mt-3 border border-neutral-800 divide-y divide-neutral-900 max-h-64 overflow-y-auto" data-testid="price-drop-recipients">
                {preview.recipients.map((r, i) => (
                  <div key={i} className="flex items-center gap-3 px-3 py-2">
                    <span className={r.skip ? "text-neutral-600 line-through" : "text-white"}>{r.email || "(no email)"}</span>
                    <span className="text-neutral-500">{r.name}</span>
                    {r.skip && <span className="ml-auto text-[9px] tracking-widest text-amber-500">{r.skip.toUpperCase()}</span>}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="px-5 py-4 border-t border-neutral-800 flex items-center gap-3">
          <button data-testid="price-drop-confirm" onClick={fire} disabled={busy || !preview || preview.sendable === 0}
            className="min-h-[38px] px-5 bg-blaze text-white heading text-[11px] tracking-widest hover:opacity-90 disabled:opacity-40">
            {busy ? "SENDING…" : `SEND TO ${preview?.sendable ?? "…"} PEOPLE`}
          </button>
          <button data-testid="price-drop-cancel" onClick={onClose} className="min-h-[38px] px-4 border border-neutral-700 text-neutral-400 text-[10px] tracking-widest hover:border-neutral-500">CANCEL</button>
        </div>
      </div>
    </div>
  );
}

export default function Revenue() {
  const [data, setData] = useState(null);
  const [group, setGroup] = useState("");
  const [blastOpen, setBlastOpen] = useState(false);

  const load = () => api.get("/admin/revenue").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!data) return <div className="px-8 py-10 text-neutral-500 text-sm" data-testid="hq-revenue-loading">Loading revenue…</div>;
  const s = data.summary;
  const rows = data.transactions.filter((t) => !group || t.group === group);
  const markSent = (sid, at) => setData((d) => ({
    ...d,
    transactions: d.transactions.map((t) => t.session_id === sid ? { ...t, recovery_email_sent_at: at } : t),
  }));

  return (
    <div className="px-4 md:px-8 py-8 max-w-6xl" data-testid="hq-revenue-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-widest text-volt">ADMIN HQ · GROWTH</div>
          <h1 className="heading text-4xl mt-1">REVENUE</h1>
          <p className="text-neutral-400 mt-2 text-sm max-w-2xl">
            Every Stripe checkout the platform has created — who actually paid, who is mid-checkout, and who abandoned.
          </p>
        </div>
        <button data-testid="price-drop-blast-btn" onClick={() => setBlastOpen(true)}
          className="flex items-center gap-2 min-h-[38px] px-4 border border-blaze text-blaze heading text-[10px] tracking-widest hover:bg-blaze hover:text-white transition-colors">
          <Megaphone size={13} /> PRICE-DROP BLAST…
        </button>
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="revenue-summary">
        <StatCard label="TOTAL COLLECTED" value={usd(s.total_collected)} sub={`${s.paid_count} paid transactions`} good={s.total_collected > 0} testid="rev-total" />
        <StatCard label="LAST 30 DAYS" value={usd(s.collected_30d)} testid="rev-30d" />
        <StatCard label="ACTIVE SUBSCRIPTIONS" value={s.active_subscriptions} good={s.active_subscriptions > 0} testid="rev-active-subs" />
        <StatCard label="LEGACY FOUNDERS SUBS" value={s.founders_subscriptions} testid="rev-founders" />
        <StatCard label="PENDING CHECKOUTS" value={s.pending_count} bad={s.pending_count > 0} testid="rev-pending" />
        <StatCard label="ABANDONED" value={s.abandoned_count} sub={`${usd(s.abandoned_value)} left on table`} testid="rev-abandoned" />
      </div>

      {data.subscriptions.length > 0 && (
        <div className="mt-8">
          <h2 className="heading text-lg">SUBSCRIPTIONS</h2>
          <div className="mt-3 border border-neutral-800 divide-y divide-neutral-800" data-testid="revenue-subs-table">
            {data.subscriptions.map((sub, i) => (
              <div key={i} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-xs bg-appcard" data-testid={`revenue-sub-row-${i}`}>
                <span className="text-white font-medium">{sub.user_email || sub.user_id || "unknown user"}</span>
                <span className="text-neutral-400">{sub.label}</span>
                {sub.founding_member && (
                  <span className="flex items-center gap-1 text-[9px] tracking-widest border border-volt/50 text-volt px-1.5 py-0.5"><Crown size={9} /> FOUNDER</span>
                )}
                <span className={`ml-auto text-[9px] tracking-widest px-1.5 py-0.5 border ${sub.status === "active" && !sub.expired ? "border-emerald-600 text-emerald-400" : "border-neutral-700 text-neutral-500"}`}>
                  {sub.expired ? "EXPIRED" : (sub.status || "").toUpperCase()}
                </span>
                <span className="text-neutral-500">until {sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString() : "—"}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 flex items-center gap-2 flex-wrap">
        <h2 className="heading text-lg mr-2">TRANSACTIONS</h2>
        {GROUPS.map((g) => (
          <button key={g.k} data-testid={`revenue-filter-${g.k || "all"}`} onClick={() => setGroup(g.k)}
            className={`px-3 py-1.5 text-[10px] tracking-widest border ${group === g.k ? "border-blaze text-blaze" : "border-neutral-700 text-neutral-500 hover:border-neutral-500"}`}>
            {g.l.toUpperCase()}
          </button>
        ))}
      </div>

      <div className="mt-3 border border-neutral-800 divide-y divide-neutral-800" data-testid="revenue-txn-table">
        {rows.length === 0 && (
          <div className="px-4 py-8 text-center text-neutral-500 text-sm" data-testid="revenue-txn-empty">No transactions in this view yet.</div>
        )}
        {rows.map((t, i) => (
          <div key={t.session_id || i} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-xs bg-appcard" data-testid={`revenue-txn-row-${i}`}>
            {t.group === "paid" ? <CheckCircle2 size={13} className="text-emerald-400 shrink-0" />
              : t.group === "pending" ? <Clock size={13} className="text-amber-400 shrink-0" />
              : <XCircle size={13} className="text-neutral-600 shrink-0" />}
            <span className="text-white font-medium">{t.user_email || "guest"}</span>
            <span className="text-neutral-400">{t.label}</span>
            {t.founding_member_discount && (
              <span className="text-[9px] tracking-widest border border-volt/50 text-volt px-1.5 py-0.5">FOUNDERS PRICE</span>
            )}
            <span className={`ml-auto heading text-sm ${t.group === "paid" ? "text-emerald-400" : "text-neutral-400"}`}>{usd(t.amount)}</span>
            <span className={`text-[9px] tracking-widest px-1.5 py-0.5 border ${groupBadge(t.group)}`}>{t.group.toUpperCase()}</span>
            <span className="text-neutral-500">{t.created_at ? new Date(t.created_at).toLocaleString() : ""}</span>
            {t.group === "abandoned" && (t.user_email || t.user_id) && (
              <span className="flex items-center gap-2">
                <ResendOfferButton txn={t} onSent={markSent} />
                {t.recovery_email_sent_at && (
                  <span className="text-[9px] text-neutral-500" data-testid={`offer-sent-at-${t.session_id}`}>
                    offer sent {new Date(t.recovery_email_sent_at).toLocaleDateString()}
                  </span>
                )}
              </span>
            )}
          </div>
        ))}
      </div>

      {blastOpen && <PriceDropModal onClose={() => setBlastOpen(false)} onDone={() => { setBlastOpen(false); load(); }} />}
    </div>
  );
}
